import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { css } from "styled-system/css";
import { periodWindow, toPeriod } from "~/lib/period";
import { Ledger } from "~/components/charts";
import { api, type PaginatedResponse } from "~/lib/api";
import { prsDek, prsHeadline } from "../story";
import { fmtFixed, inPeriod, periodName } from "~/lib/format";
import { ed, btn, field, tbl } from "~/components/editorial";

interface JiraIssue {
  id: string;
  key: string;
  summary: string;
  issueType: string;
  status: string;
  storyPoints: number | null;
}

type WorkCategory =
  | "feature"
  | "bug"
  | "tech_debt"
  | "docs"
  | "release"
  | "infra"
  | "other";

interface PullRequest {
  id: string;
  number: number;
  title: string;
  state: "open" | "merged" | "closed";
  authorId: string | null;
  authorName: string | null;
  authorAvatarUrl: string | null;
  repoId: string;
  repoName: string;
  repoFullName: string;
  additions: number;
  deletions: number;
  comments: number;
  createdAt: string;
  mergedAt: string | null;
  closedAt: string | null;
  timeToMerge: number | null;
  timeToFirstReview: number | null;
  timeOpen: number | null;
  daysOpen: number | null;
  totalLinesChanged: number;
  isLargePr: boolean;
  isStale: boolean;
  isVeryStale: boolean;
  hasBadTitle: boolean;
  workCategory: WorkCategory | null;
  jiraIssue: JiraIssue | null;
}

type SortKey =
  | "createdAt"
  | "additions"
  | "comments"
  | "timeToMerge"
  | "timeOpen";

function SortTh({
  k,
  label,
  sort,
  order,
  onSort,
}: {
  k: SortKey;
  label: string;
  sort: SortKey;
  order: "asc" | "desc";
  onSort: (key: SortKey) => void;
}) {
  return (
    <th
      data-num
      aria-sort={
        sort === k ? (order === "asc" ? "ascending" : "descending") : undefined
      }
    >
      <button type="button" onClick={() => onSort(k)}>
        {label}
        {sort === k ? (order === "asc" ? " ↑" : " ↓") : ""}
      </button>
    </th>
  );
}

interface PullRequestsTabProps {
  range: string;
}

const CATEGORY_LABELS: Record<WorkCategory, string> = {
  feature: "Feature",
  bug: "Bug fix",
  tech_debt: "Tech debt",
  docs: "Docs",
  release: "Release",
  infra: "Infra",
  other: "Other",
};

const STATES = ["all", "open", "merged", "closed"] as const;

export function PullRequestsTab({ range }: PullRequestsTabProps) {
  const [state, setState] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortKey>("createdAt");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);
  const limit = 50;

  // Period window: start of the month/quarter/year through end of today.
  // Both bounds are stable within a day, so the query key stays put.
  const periodWin = periodWindow(toPeriod(range));
  const from = periodWin.from.toISOString();
  const to = periodWin.to.toISOString();
  const now = periodName(periodWin.label);

  const { data, isLoading } = useQuery({
    queryKey: ["prs", { state, from, to, page, limit, range }],
    queryFn: () => {
      const params = new URLSearchParams({
        from,
        to,
        page: String(page),
        limit: String(limit),
      });
      if (state !== "all") params.set("state", state);
      return api.get<PaginatedResponse<PullRequest>>(`/prs?${params}`);
    },
    staleTime: 30 * 1000,
  });

  const prs = data?.data.items ?? [];
  const total = data?.data.total ?? 0;
  const hasMore = data?.data.hasMore ?? false;

  // Summary stats (for the PRs on this page)
  const openPrs = prs.filter((pr) => pr.state === "open");
  const stalePrs = prs.filter((pr) => pr.isStale || pr.isVeryStale);
  const largePrs = prs.filter((pr) => pr.isLargePr);
  const badTitlePrs = prs.filter((pr) => pr.hasBadTitle);

  // Client-side filter by search (title)
  const filteredPrs = search
    ? prs.filter((pr) => pr.title.toLowerCase().includes(search.toLowerCase()))
    : prs;

  // Client-side sort
  const sortedPrs = [...filteredPrs].sort((a, b) => {
    let aVal: number | string | null = null;
    let bVal: number | string | null = null;

    switch (sort) {
      case "createdAt":
        aVal = new Date(a.createdAt).getTime();
        bVal = new Date(b.createdAt).getTime();
        break;
      case "additions":
        aVal = a.totalLinesChanged;
        bVal = b.totalLinesChanged;
        break;
      case "comments":
        aVal = a.comments;
        bVal = b.comments;
        break;
      case "timeToMerge":
        aVal = a.timeToMerge ?? Infinity;
        bVal = b.timeToMerge ?? Infinity;
        break;
      case "timeOpen":
        aVal = a.timeOpen ?? -1;
        bVal = b.timeOpen ?? -1;
        break;
    }

    if (aVal === null || bVal === null) return 0;
    if (aVal < bVal) return order === "asc" ? -1 : 1;
    if (aVal > bVal) return order === "asc" ? 1 : -1;
    return 0;
  });

  const handleSort = (key: SortKey) => {
    if (sort === key) {
      setOrder(order === "asc" ? "desc" : "asc");
    } else {
      setSort(key);
      setOrder("desc");
    }
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });

  const formatTimeOpen = (hours: number | null) => {
    if (hours === null) return null;
    const days = Math.floor(hours / 24);
    return days === 0 ? `${Math.round(hours)}h` : `${days}d`;
  };

  const getPrUrl = (pr: PullRequest) =>
    `https://github.com/${pr.repoFullName}/pull/${pr.number}`;

  const sortProps = { sort, order, onSort: handleSort };

  return (
    <>
      <div className={ed.kicker}>Pull requests · {now}</div>
      <h1 className={ed.headlineCompact}>
        {isLoading ? "Pull requests" : prsHeadline(total, state, now)}
      </h1>
      {!isLoading && (
        <p className={ed.dek}>
          {prsDek(
            prs.length,
            stalePrs.length,
            largePrs.length,
            badTitlePrs.length,
          )}
        </p>
      )}
      <div className={ed.byline}>
        <b>Pull requests</b> · opened {inPeriod(periodWin.label)}. Counts beside the total
        are for the <span className="num">{fmtFixed(prs.length)}</span> PRs on
        this page. From GitHub.
      </div>

      <Ledger
        hideMissingPrior
        columns="fit"
        items={[
          {
            label: state === "all" ? "PRs opened" : `${state} PRs`,
            value: fmtFixed(total),
            note: "in period",
          },
          {
            label: "Still open",
            value: fmtFixed(openPrs.length),
            note: "on this page",
          },
          {
            label: "Stale",
            value: fmtFixed(stalePrs.length),
            note: "open 7+ days",
          },
          {
            label: "Large",
            value: fmtFixed(largePrs.length),
            note: "500+ lines",
          },
          {
            label: "Unclear title",
            value: fmtFixed(badTitlePrs.length),
            note: "WIP, fix, update…",
          },
        ]}
      />

      <div className={ed.sect}>The list</div>
      <h2 className={ed.h2}>Every pull request, newest first</h2>
      <div className={tbl.controls}>
        <div className={field.segs} role="group" aria-label="State">
          {STATES.map((s) => (
            <button
              key={s}
              type="button"
              aria-pressed={state === s}
              onClick={() => {
                setState(s);
                setPage(1);
              }}
            >
              {s}
            </button>
          ))}
        </div>
        <input
          type="search"
          placeholder="Search titles on this page"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={field.search}
          aria-label="Search pull requests"
        />
        <span className={ed.num}>
          {fmtFixed(sortedPrs.length)} of {fmtFixed(prs.length)}
        </span>
      </div>

      {isLoading ? (
        <div className={ed.loading}>Loading pull requests…</div>
      ) : (
        <div className={tbl.scroll}>
          <table className={tbl.numeric}>
            <thead>
              <tr>
                <th data-num>PR</th>
                <th>Title and flags</th>
                <th>Repo</th>
                <th>Ticket</th>
                <th>Kind</th>
                <th>Author</th>
                <th>State</th>
                <SortTh k="additions" label="+/−" {...sortProps} />
                <SortTh k="comments" label="Cmts" {...sortProps} />
                <SortTh k="timeOpen" label="Open / to merge" {...sortProps} />
                <SortTh k="createdAt" label="Created" {...sortProps} />
              </tr>
            </thead>
            <tbody>
              {sortedPrs.map((pr) => (
                <tr key={pr.id}>
                  <td data-num data-dim>
                    {pr.number}
                  </td>
                  <td data-text className={styles.title}>
                    <a
                      href={getPrUrl(pr)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {pr.title}
                    </a>
                    {pr.isVeryStale ? (
                      <span
                        className={tbl.flag}
                        data-tone="solid"
                        title="Open 14+ days"
                      >
                        Very stale
                      </span>
                    ) : pr.isStale ? (
                      <span className={tbl.flag} title="Open 7+ days">
                        Stale
                      </span>
                    ) : null}
                    {pr.isLargePr && (
                      <span className={tbl.flag} title="500+ lines changed">
                        Large
                      </span>
                    )}
                    {pr.hasBadTitle && (
                      <span
                        className={tbl.flag}
                        title="Title too vague to review from"
                      >
                        Unclear title
                      </span>
                    )}
                  </td>
                  <td data-text className={styles.dimText}>
                    {pr.repoName}
                  </td>
                  <td title={pr.jiraIssue?.summary}>
                    {pr.jiraIssue ? (
                      <>
                        {pr.jiraIssue.key}
                        {pr.jiraIssue.storyPoints !== null && (
                          <span data-dim> {pr.jiraIssue.storyPoints}sp</span>
                        )}
                      </>
                    ) : (
                      <span data-dim>–</span>
                    )}
                  </td>
                  <td data-text className={styles.dimText}>
                    {pr.workCategory
                      ? CATEGORY_LABELS[pr.workCategory] ?? pr.workCategory
                      : "–"}
                  </td>
                  <td data-text className={styles.nowrap}>
                    {pr.authorName || "Unknown"}
                  </td>
                  <td data-text className={styles.state} data-state={pr.state}>
                    {pr.state}
                  </td>
                  <td data-num>
                    +{fmtFixed(pr.additions)}{" "}
                    <span data-dim>−{fmtFixed(pr.deletions)}</span>
                  </td>
                  <td data-num data-dim={pr.comments === 0 || undefined}>
                    {pr.comments}
                  </td>
                  <td data-num>
                    {pr.state === "open" && pr.timeOpen ? (
                      <span className={styles.open}>
                        {formatTimeOpen(pr.timeOpen)} open
                      </span>
                    ) : pr.timeToMerge ? (
                      `${pr.timeToMerge.toFixed(1)}h`
                    ) : (
                      <span data-dim>–</span>
                    )}
                  </td>
                  <td data-num>{formatDate(pr.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {sortedPrs.length === 0 && (
            <div className={ed.loading}>No pull requests found.</div>
          )}
        </div>
      )}
      <p className={ed.methodNarrow}>
        Stale: open 7+ days (very stale, 14+). Large: 500+ lines changed.
        Unclear title: vague words like WIP, fix or update with nothing
        specific. Kind from the linked JIRA issue type, else the branch and
        title.
      </p>

      {total > limit && (
        <div className={tbl.pager}>
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className={btn.outline}
          >
            Previous
          </button>
          <span>
            Page {page} of {Math.ceil(total / limit)} ({fmtFixed(total)} PRs)
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => p + 1)}
            disabled={!hasMore}
            className={btn.outline}
          >
            Next
          </button>
        </div>
      )}
    </>
  );
}

const styles = {
  title: css({
    minWidth: "320px",
    lineHeight: 1.35,
    "& a": { fontWeight: 600 },
  }),
  dimText: css({
    color: "ed.ink2!",
    whiteSpace: "nowrap!",
    fontSize: "11.5px",
  }),
  nowrap: css({ whiteSpace: "nowrap!" }),
  state: css({
    textTransform: "lowercase",
    whiteSpace: "nowrap!",
    color: "ed.ink2!",
    "&[data-state=open]": { color: "ed.ink!", fontWeight: 600 },
  }),
  open: css({ fontWeight: 600 }),
};
